import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  TablePagination,
  Chip,
  Avatar,
  InputAdornment,
  Skeleton,
  Grid,
  IconButton,
  Tooltip as MuiTooltip,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import {
  MdDelete,
  MdEdit,
  MdAdd,
  MdSearch,
  MdVisibility,
  MdEventNote,
  MdAccessTime,
  MdLocationOn,
  MdMap,
  MdPerson,
  MdPhone,
  MdGridView,
  MdViewList,
  MdRefresh,
  MdClear,
  MdContentCopy,
  MdShare,
  MdToday,
  MdFileUpload,
  MdFileDownload,
  MdCloudUpload,
  MdCheckCircle,
  MdWarning,
  MdFilterList,
  MdSort,
  MdAssignmentInd,
  MdPendingActions,
  MdLocalShipping,
  MdLaunch,
} from "react-icons/md";
import toast from "react-hot-toast";
import {
  getAllAppointments,
  createAppointment,
  updateAppointment,
  deleteAppointment,
  bulkCreateAppointments,
} from "../apis/appointmentApi";
import { getAllEmployees } from "../apis/employeeApi";
import { brand } from "../theme";

const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith("http://") || path.startsWith("https://") || path.startsWith("data:")) {
    return path;
  }
  const cleanPath = path.startsWith("/") ? path.slice(1) : path;
  if (cleanPath.startsWith("uploads/")) {
    return `https://skishore.in/api/${cleanPath}`;
  }
  if (!cleanPath.startsWith("api/")) {
    return `https://skishore.in/api/${cleanPath}`;
  }
  return `https://skishore.in/${cleanPath}`;
};

const initials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");

// Field staff work status: assigned → in_progress → completed, set by the
// staff app's Start Work / Complete Work camera-proof flow.
const workStatusChipStyle = (status) => {
  const s = String(status || "assigned").toLowerCase();
  if (s === "completed") {
    return { bg: `${brand.success}1E`, color: brand.success, icon: <MdCheckCircle size={14} />, label: "Completed" };
  }
  if (s === "in_progress" || s === "in progress") {
    return { bg: "#3B82F61E", color: "#3B82F6", icon: <MdLocalShipping size={14} />, label: "In Progress" };
  }
  return { bg: `${brand.brass}20`, color: "#B4800A", icon: <MdPendingActions size={14} />, label: "Assigned" };
};

const StatusChip = ({ status }) => {
  const { bg, color, icon, label } = workStatusChipStyle(status);
  return (
    <Chip
      icon={icon}
      label={label}
      size="small"
      sx={{ bgcolor: bg, color, fontWeight: 700, fontSize: "0.75rem", "& .MuiChip-icon": { color } }}
    />
  );
};

const StatCard = ({ icon, label, count, accent }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      borderRadius: "20px",
      display: "flex",
      alignItems: "center",
      gap: 2,
      bgcolor: brand.paper,
      border: `1px solid ${brand.slateLight}40`,
      boxShadow: "0 10px 30px -15px rgba(11,10,61,0.06)",
      transition: "transform 200ms ease, box-shadow 200ms ease",
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: "0 18px 40px -18px rgba(11,10,61,0.12)",
        borderColor: `${brand.brass}66`,
      },
    }}
  >
    <Box
      sx={{
        width: 46,
        height: 46,
        borderRadius: "14px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: `${accent || brand.brass}18`,
        color: accent || brand.ink950,
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box minWidth={0}>
      <Typography
        sx={{
          fontFamily: '"Fraunces", serif',
          fontWeight: 700,
          fontSize: "1.8rem",
          color: brand.ink950,
          lineHeight: 1.1,
        }}
      >
        {count}
      </Typography>
      <Typography sx={{ color: brand.slate, fontSize: "0.82rem", fontWeight: 600 }} noWrap>
        {label}
      </Typography>
    </Box>
  </Paper>
);

const Appointment = () => {
  const [openForm, setOpenForm] = useState(false);
  const [openView, setOpenView] = useState(false);
  const [editing, setEditing] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [viewData, setViewData] = useState(null);

  // Bulk Excel state
  const [openBulkDialog, setOpenBulkDialog] = useState(false);
  const [uploadingExcel, setUploadingExcel] = useState(false);
  const [savingBulk, setSavingBulk] = useState(false);
  const [bulkPreview, setBulkPreview] = useState([]);

  // Filter & Sorting states
  const [appointments, setAppointments] = useState([]);
  const [filterDate, setFilterDate] = useState("");
  const [dateCategoryFilter, setDateCategoryFilter] = useState("all"); // 'all' | 'today' | 'upcoming' | 'past'
  const [dateSortOrder, setDateSortOrder] = useState("desc"); // 'desc' | 'asc'
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [viewMode, setViewMode] = useState("table"); // 'table' | 'grid'

  // Assign Staff / Employee State
  const [employees, setEmployees] = useState([]);
  const [openAssignModal, setOpenAssignModal] = useState(false);
  const [assignAppointment, setAssignAppointment] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState("");
  const [assigning, setAssigning] = useState(false);

  const loggedInUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);

  const [form, setForm] = useState({
    name: "",
    phone: "",
    date: "",
    time: "",
    location: "",
    map_url: "",
    comment: "",
    assigned_person: "",
  });

  const loadAppointments = async () => {
    setLoading(true);
    try {
      const res = await getAllAppointments();
      setAppointments(Array.isArray(res) ? res : []);
    } catch {
      toast.error("Failed to fetch appointments");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAppointments();
    getAllEmployees().then((res) => {
      setEmployees(Array.isArray(res) ? res : []);
    });
  }, []);

  /* ------------------ EXCEL HANDLERS ------------------ */

  const handleDownloadTemplate = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Appointments");

      worksheet.columns = [
        { header: "Customer Name *", key: "customer_name", width: 25 },
        { header: "Phone *", key: "phone", width: 18 },
        { header: "Appointment Date (YYYY-MM-DD) *", key: "appointment_date", width: 30 },
        { header: "Appointment Time (HH:MM) *", key: "appointment_time", width: 25 },
        { header: "Assigned Staff", key: "assigned_person", width: 22 },
        { header: "Location", key: "location", width: 25 },
        { header: "Google Map URL", key: "map_url", width: 35 },
        { header: "Comment / Notes", key: "comment", width: 30 },
      ];

      // Format header row
      const headerRow = worksheet.getRow(1);
      headerRow.font = { bold: true, color: { argb: "FFFFFF" }, size: 11 };
      headerRow.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "0B0A3D" },
      };

      const todayStr = new Date().toISOString().split("T")[0];
      worksheet.addRow({
        customer_name: "Rahul Sharma",
        phone: "9876543210",
        appointment_date: todayStr,
        appointment_time: "10:30",
        assigned_person: "Alex Miller",
        location: "Sector 18, Noida",
        map_url: "https://maps.google.com/?q=Noida",
        comment: "Initial Consultation",
      });

      worksheet.addRow({
        customer_name: "Priya Patel",
        phone: "9123456789",
        appointment_date: todayStr,
        appointment_time: "14:00",
        assigned_person: "Sarah Jenkins",
        location: "Connaught Place, Delhi",
        map_url: "https://maps.google.com/?q=Delhi",
        comment: "Site Visit Follow-up",
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, "appointment_upload_template.xlsx");
      toast.success("Excel template downloaded!");
    } catch (err) {
      console.error(err);
      toast.error("Failed to generate Excel template");
    }
  };

  const handleExcelFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingExcel(true);
    try {
      const buffer = await file.arrayBuffer();
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(buffer);
      const worksheet = workbook.worksheets[0];

      // Default column index mapping (matches sample template)
      const colMap = {
        name: 1,
        phone: 2,
        date: 3,
        time: 4,
        assigned: 5,
        location: 6,
        mapUrl: 7,
        comment: 8,
      };

      // Dynamic header column matching from row 1
      const headerRow = worksheet.getRow(1);
      if (headerRow && headerRow.cellCount > 0) {
        headerRow.eachCell((cell, colNumber) => {
          const h = String(cell.value || "").trim().toLowerCase();
          if (h.includes("name") && !h.includes("staff") && !h.includes("person") && !h.includes("assign")) {
            colMap.name = colNumber;
          } else if (h.includes("phone") || h.includes("mobile") || h.includes("contact")) {
            colMap.phone = colNumber;
          } else if (h.includes("date")) {
            colMap.date = colNumber;
          } else if (h.includes("time") || h.includes("slot")) {
            colMap.time = colNumber;
          } else if (h.includes("assign") || h.includes("staff") || h.includes("person") || h.includes("technician")) {
            colMap.assigned = colNumber;
          } else if (h.includes("location") || h.includes("address")) {
            colMap.location = colNumber;
          } else if (h.includes("map") || h.includes("url")) {
            colMap.mapUrl = colNumber;
          } else if (h.includes("comment") || h.includes("note") || h.includes("remark")) {
            colMap.comment = colNumber;
          }
        });
      }

      const parsedRows = [];
      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber === 1) return; // Skip header row

        const getVal = (idx) => {
          if (!idx) return "";
          const cell = row.getCell(idx);
          if (!cell || cell.value === null || cell.value === undefined) return "";
          if (cell.value instanceof Date) {
            return cell.value.toISOString().split("T")[0];
          }
          if (typeof cell.value === "object") {
            return cell.value.text || cell.value.result || String(cell.value);
          }
          return String(cell.value).trim();
        };

        const name = getVal(colMap.name);
        const phone = getVal(colMap.phone);
        let dateVal = getVal(colMap.date);
        let timeVal = getVal(colMap.time);
        const assigned = getVal(colMap.assigned);
        const location = getVal(colMap.location);
        const mapUrl = getVal(colMap.mapUrl);
        const comment = getVal(colMap.comment);

        if (dateVal && dateVal.includes("T")) {
          dateVal = dateVal.split("T")[0];
        }

        const isValid = Boolean(name && phone && dateVal && timeVal);

        if (name || phone || dateVal || timeVal || assigned) {
          parsedRows.push({
            customer_name: name,
            phone: phone,
            appointment_date: dateVal,
            appointment_time: timeVal,
            assigned_person: assigned || null,
            location: location || null,
            map_url: mapUrl || null,
            comment: comment || null,
            isValid,
            errorMsg: !isValid ? "Missing Name, Phone, Date, or Time" : null,
          });
        }
      });

      if (parsedRows.length === 0) {
        toast.error("No valid data rows found in the selected Excel file");
      } else {
        setBulkPreview(parsedRows);
        toast.success(`Parsed ${parsedRows.length} rows from Excel file`);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to parse Excel file. Please use the downloaded template.");
    } finally {
      setUploadingExcel(false);
      e.target.value = "";
    }
  };

  const handleBulkSubmit = async () => {
    const validRows = bulkPreview.filter((r) => r.isValid);
    if (validRows.length === 0) {
      toast.error("No valid appointment rows to import");
      return;
    }

    setSavingBulk(true);
    try {
      const res = await bulkCreateAppointments(validRows);
      toast.success(res.message || `Imported ${validRows.length} appointments`);
      setOpenBulkDialog(false);
      setBulkPreview([]);
      loadAppointments();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to bulk import appointments");
    } finally {
      setSavingBulk(false);
    }
  };

  /* ------------------ STATS & FILTERED DATA ------------------ */

  const stats = useMemo(() => {
    const todayStr = new Date().toISOString().split("T")[0];
    let todayCount = 0;
    let assignedCount = 0;
    let locationCount = 0;

    appointments.forEach((a) => {
      if (a.appointment_date === todayStr) todayCount += 1;
      if (a.assigned_person && a.assigned_person.trim() !== "") assignedCount += 1;
      if (a.location && a.location.trim() !== "") locationCount += 1;
    });

    return {
      total: appointments.length,
      todayCount,
      assignedCount,
      locationCount,
    };
  }, [appointments]);

  const filteredAppointments = useMemo(() => {
    const todayStr = new Date().toISOString().split("T")[0];

    const filtered = appointments.filter((a) => {
      // Date Category Filter
      if (dateCategoryFilter === "today" && a.appointment_date !== todayStr) return false;
      if (dateCategoryFilter === "upcoming" && a.appointment_date < todayStr) return false;
      if (dateCategoryFilter === "past" && a.appointment_date >= todayStr) return false;

      // Specific Date Input Filter
      const matchDate = filterDate ? a.appointment_date === filterDate : true;
      if (!matchDate) return false;

      // Search Query
      if (!search.trim()) {
        // Auto-filter for vendor or staff employee to show only their assigned appointments
        const userRole = (loggedInUser?.role || "").toLowerCase();
        if ((userRole === "vendor" || userRole === "employee" || userRole === "staff") && loggedInUser?.name) {
          if (String(a.assigned_person || "").toLowerCase() !== loggedInUser.name.toLowerCase()) {
            return false;
          }
        }
        return true;
      }

      // Auto-filter for vendor or staff employee to show only their assigned appointments
      const userRole = (loggedInUser?.role || "").toLowerCase();
      if ((userRole === "vendor" || userRole === "employee" || userRole === "staff") && loggedInUser?.name) {
        if (String(a.assigned_person || "").toLowerCase() !== loggedInUser.name.toLowerCase()) {
          return false;
        }
      }
      const q = search.toLowerCase();
      const name = (a.customer_name || "").toLowerCase();
      const phone = (a.phone || "").toLowerCase();
      const assigned = (a.assigned_person || "").toLowerCase();
      const loc = (a.location || "").toLowerCase();

      return name.includes(q) || phone.includes(q) || assigned.includes(q) || loc.includes(q);
    });

    // Date-Wise Sort (chronological or reverse chronological)
    return filtered.sort((a, b) => {
      const dtA = `${a.appointment_date || ""} ${a.appointment_time || ""}`;
      const dtB = `${b.appointment_date || ""} ${b.appointment_time || ""}`;
      return dateSortOrder === "desc" ? dtB.localeCompare(dtA) : dtA.localeCompare(dtB);
    });
  }, [appointments, filterDate, dateCategoryFilter, search, dateSortOrder]);

  const paginatedAppointments = useMemo(() => {
    return filteredAppointments.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredAppointments, page, rowsPerPage]);

  /* ------------------ HANDLERS ------------------ */

  const openAdd = () => {
    setEditing(false);
    setSelectedId(null);
    setForm({
      name: "",
      phone: "",
      date: "",
      time: "",
      location: "",
      map_url: "",
      comment: "",
      assigned_person: "",
    });
    setOpenForm(true);
  };

  const openEdit = (data) => {
    setEditing(true);
    setSelectedId(data.id || data._id);
    setForm({
      name: data.customer_name || "",
      phone: data.phone || "",
      date: data.appointment_date || "",
      time: data.appointment_time || "",
      location: data.location || "",
      map_url: data.map_url || "",
      comment: data.comment || "",
      assigned_person: data.assigned_person || "",
    });
    setOpenForm(true);
  };

  const openViewDialog = (data) => {
    setViewData(data);
    setOpenView(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!form.name || !form.phone || !form.date || !form.time) {
      toast.error("Please fill required fields (Name, Phone, Date, Time)");
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("customer_name", form.name);
      formData.append("phone", form.phone);
      formData.append("appointment_date", form.date);
      formData.append("appointment_time", form.time);
      formData.append("location", form.location);
      formData.append("comment", form.comment);
      formData.append("assigned_person", form.assigned_person);
      formData.append("map_url", form.map_url);

      if (editing) {
        await updateAppointment(selectedId, formData);
        toast.success("Appointment updated");
      } else {
        await createAppointment(formData);
        toast.success("Appointment created");
      }

      setOpenForm(false);
      loadAppointments();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleOpenAssign = (item) => {
    setAssignAppointment(item);
    setSelectedStaff(item.assigned_person || "");
    setOpenAssignModal(true);
  };

  const handleSaveAssign = async () => {
    if (!assignAppointment) return;
    setAssigning(true);
    try {
      const id = assignAppointment.id || assignAppointment._id;
      const formData = new FormData();
      formData.append("customer_name", assignAppointment.customer_name || "");
      formData.append("phone", assignAppointment.phone || "");
      formData.append("appointment_date", assignAppointment.appointment_date || "");
      formData.append("appointment_time", assignAppointment.appointment_time || "");
      formData.append("location", assignAppointment.location || "");
      formData.append("comment", assignAppointment.comment || "");
      formData.append("assigned_person", selectedStaff);
      formData.append("map_url", assignAppointment.map_url || "");

      await updateAppointment(id, formData);
      toast.success(`Appointment #${id} assigned to ${selectedStaff || "Unassigned"}`);
      setOpenAssignModal(false);
      setAssignAppointment(null);
      loadAppointments();
    } catch {
      toast.error("Failed to update staff assignment");
    } finally {
      setAssigning(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this appointment record?")) return;

    setDeletingId(id);
    try {
      await deleteAppointment(id);
      toast.success("Appointment deleted");
      loadAppointments();
    } catch {
      toast.error("Delete failed");
    } finally {
      setDeletingId(null);
    }
  };

  const copyReceipt = (data) => {
    const text = `
Appointment Receipt — OurGenie

Customer: ${data.customer_name}
Mobile: ${data.phone}
Assigned To: ${data.assigned_person || "Unassigned"}
Date: ${data.appointment_date}
Time: ${data.appointment_time}
Location: ${data.location || "N/A"}
Map: ${data.map_url || "N/A"}
Comment: ${data.comment || "N/A"}
`;
    navigator.clipboard.writeText(text);
    toast.success("Receipt copied to clipboard");
  };

  const shareReceipt = async (data) => {
    const text = `
Appointment Ticket
Customer: ${data.customer_name}
Mobile: ${data.phone}
Date: ${data.appointment_date} • ${data.appointment_time}
Location: ${data.location || "N/A"}
Map: ${data.map_url || "N/A"}

— OurGenie Command Center
`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "Appointment Receipt", text });
      } catch (err) {
        // User cancelled share
      }
    } else {
      navigator.clipboard.writeText(text);
      toast.success("Copied receipt link!");
    }
  };

  return (
    <Box p={{ xs: 2, sm: 3, md: 4 }} sx={{ maxWidth: 1600, margin: "0 auto" }}>
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <Box
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        gap={2}
        mb={3}
      >
        <Box>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Typography
              variant="h3"
              sx={{
                color: brand.ink950,
                fontFamily: '"Fraunces", serif',
                fontWeight: 700,
                fontSize: { xs: "1.8rem", md: "2.2rem" },
              }}
            >
              {(loggedInUser?.role || "").toLowerCase() === "vendor" ? "My Assigned Appointments" : "Appointment Operations"}
            </Typography>
            <Chip
              label={`${filteredAppointments.length} Scheduled`}
              size="small"
              sx={{
                bgcolor: (loggedInUser?.role || "").toLowerCase() === "vendor" ? "#8B5CF622" : brand.brassSoft,
                color: (loggedInUser?.role || "").toLowerCase() === "vendor" ? "#6D28D9" : brand.ink950,
                fontWeight: 700,
              }}
            />
          </Box>
          <Typography sx={{ color: brand.slate, fontSize: "0.85rem", mt: 0.5 }}>
            {(loggedInUser?.role || "").toLowerCase() === "vendor"
              ? "Service appointments assigned to your vendor account for execution and customer fulfillment"
              : "Schedule, manage, and dispatch service appointments and field assignments"}
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
          <MuiTooltip title="Reload appointment bookings">
            <IconButton
              onClick={loadAppointments}
              sx={{
                bgcolor: brand.paper,
                border: `1px solid ${brand.slateLight}60`,
                p: 1.1,
                borderRadius: "14px",
                color: brand.ink950,
                "&:hover": { bgcolor: brand.brassSoft },
              }}
            >
              <MdRefresh size={20} className={loading ? "animate-spin" : ""} />
            </IconButton>
          </MuiTooltip>

          <Button
            variant="outlined"
            onClick={() => setOpenBulkDialog(true)}
            startIcon={<MdFileUpload size={20} />}
            sx={{
              px: 2.2,
              py: 1,
              borderRadius: "14px",
              fontWeight: 700,
              fontSize: "0.88rem",
              borderColor: brand.ink950,
              color: brand.ink950,
              "&:hover": { bgcolor: `${brand.ink950}0B`, borderColor: brand.ink950 },
            }}
          >
            Bulk Excel Upload
          </Button>

          <Button
            variant="contained"
            color="secondary"
            onClick={openAdd}
            startIcon={<MdAdd size={20} />}
            sx={{
              px: 2.5,
              py: 1,
              borderRadius: "14px",
              fontWeight: 700,
              fontSize: "0.88rem",
              boxShadow: "0 8px 20px -6px rgba(201,162,39,0.5)",
            }}
          >
            Add Appointment
          </Button>
        </Box>
      </Box>

      {/* ── Stat Cards Grid ─────────────────────────────────────────────── */}
      <Grid container spacing={2.5} mb={3.5}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<MdEventNote size={24} />} label="Total Bookings" count={stats.total} accent="#3B82F6" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<MdToday size={24} />} label="Scheduled Today" count={stats.todayCount} accent={brand.brass} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<MdPerson size={24} />}
            label="Assigned Field Staff"
            count={stats.assignedCount}
            accent="#10B981"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<MdLocationOn size={24} />}
            label="Location Specified"
            count={stats.locationCount}
            accent={brand.ink950}
          />
        </Grid>
      </Grid>

      {/* ── Filter Controls Bar ─────────────────────────────────────────── */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: "20px",
          bgcolor: brand.paper,
          border: `1px solid ${brand.slateLight}40`,
          mb: 3,
        }}
      >
        <Box
          display="flex"
          flexDirection={{ xs: "column", md: "row" }}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
          gap={2}
        >
          <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
            {/* Search Input */}
            <TextField
              placeholder="Search customer, phone, location..."
              size="small"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              sx={{
                width: { xs: "100%", sm: 260 },
                "& .MuiOutlinedInput-root": {
                  borderRadius: "14px",
                  bgcolor: `${brand.slateLight}15`,
                  fontSize: "0.85rem",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MdSearch color={brand.slate} size={20} />
                  </InputAdornment>
                ),
              }}
            />

            {/* Date Filter */}
            <TextField
              type="date"
              size="small"
              label="Specific Date"
              value={filterDate}
              onChange={(e) => {
                setFilterDate(e.target.value);
                setPage(0);
              }}
              InputLabelProps={{ shrink: true }}
              sx={{
                width: { xs: "100%", sm: 160 },
                "& .MuiOutlinedInput-root": {
                  borderRadius: "14px",
                  bgcolor: `${brand.slateLight}15`,
                  fontSize: "0.85rem",
                },
              }}
            />

            {/* Quick Date Chips */}
            <ToggleButtonGroup
              value={dateCategoryFilter}
              exclusive
              onChange={(_, v) => {
                if (v) {
                  setDateCategoryFilter(v);
                  setPage(0);
                }
              }}
              size="small"
              sx={{
                bgcolor: `${brand.slateLight}15`,
                p: 0.4,
                borderRadius: "12px",
                "& .MuiToggleButton-root": {
                  border: "none",
                  borderRadius: "9px",
                  px: 1.5,
                  py: 0.5,
                  fontSize: "0.78rem",
                  fontWeight: 600,
                  color: brand.slate,
                  "&.Mui-selected": {
                    bgcolor: brand.ink950,
                    color: brand.parchment,
                  },
                },
              }}
            >
              <ToggleButton value="all">All Dates</ToggleButton>
              <ToggleButton value="today">Today</ToggleButton>
              <ToggleButton value="upcoming">Upcoming</ToggleButton>
              <ToggleButton value="past">Past</ToggleButton>
            </ToggleButtonGroup>

            {/* Date Sort Toggle */}
            <MuiTooltip title={dateSortOrder === "desc" ? "Sorted Newest First (Click to reverse)" : "Sorted Oldest First (Click to reverse)"}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setDateSortOrder((prev) => (prev === "desc" ? "asc" : "desc"))}
                startIcon={<MdSort />}
                sx={{
                  borderRadius: "12px",
                  borderColor: `${brand.slateLight}60`,
                  color: brand.ink950,
                  fontSize: "0.78rem",
                  fontWeight: 600,
                  textTransform: "none",
                }}
              >
                {dateSortOrder === "desc" ? "Newest First" : "Oldest First"}
              </Button>
            </MuiTooltip>

            {(filterDate || search || dateCategoryFilter !== "all") && (
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={() => {
                  setFilterDate("");
                  setSearch("");
                  setDateCategoryFilter("all");
                }}
                startIcon={<MdClear />}
                sx={{ borderRadius: "10px", fontSize: "0.78rem" }}
              >
                Clear
              </Button>
            )}
          </Box>

          {/* View Switcher */}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, v) => v && setViewMode(v)}
            size="small"
            sx={{
              bgcolor: `${brand.slateLight}20`,
              p: 0.4,
              borderRadius: "12px",
              "& .MuiToggleButton-root": {
                border: "none",
                borderRadius: "9px",
                px: 1.5,
                py: 0.5,
                color: brand.slate,
                "&.Mui-selected": {
                  bgcolor: brand.paper,
                  color: brand.ink950,
                  boxShadow: "0 2px 8px rgba(11,10,61,0.08)",
                },
              },
            }}
          >
            <ToggleButton value="table">
              <MdViewList size={18} />
            </ToggleButton>
            <ToggleButton value="grid">
              <MdGridView size={18} />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Paper>

      {/* ── Table vs Grid View ─────────────────────────────────────────── */}
      {viewMode === "table" ? (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            borderRadius: "22px",
            overflow: "hidden",
            border: `1px solid ${brand.slateLight}40`,
            bgcolor: brand.paper,
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>#</TableCell>
                <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>Customer Name</TableCell>
                <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>Mobile Contact</TableCell>
                <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>Assigned Staff</TableCell>
                <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>Date</TableCell>
                <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>Time Slot</TableCell>
                <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>Location</TableCell>
                <TableCell align="right" sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: rowsPerPage }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 9 }).map((__, j) => (
                      <TableCell key={j}>
                        <Skeleton variant="text" height={28} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : paginatedAppointments.length > 0 ? (
                paginatedAppointments.map((item, index) => (
                  <TableRow
                    key={item.id || item._id || index}
                    sx={{ "&:hover": { bgcolor: `${brand.brassSoft}60` } }}
                  >
                    <TableCell
                      sx={{
                        color: brand.slate,
                        fontFamily: '"IBM Plex Mono", monospace',
                        fontSize: "0.82rem",
                        fontWeight: 600,
                      }}
                    >
                      {String(page * rowsPerPage + index + 1).padStart(2, "0")}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Avatar
                          sx={{
                            width: 36,
                            height: 36,
                            fontSize: "0.85rem",
                            fontWeight: 700,
                            bgcolor: brand.ink950,
                            color: brand.parchment,
                          }}
                        >
                          {initials(item.customer_name) || <MdPerson size={20} />}
                        </Avatar>
                        <Typography sx={{ fontWeight: 700, fontSize: "0.92rem", color: brand.ink950 }}>
                          {item.customer_name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: brand.slate, fontSize: "0.85rem", fontFamily: '"IBM Plex Mono", monospace' }}>
                      {item.phone || "—"}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={item.assigned_person || "Unassigned"}
                        size="small"
                        sx={{
                          bgcolor: item.assigned_person ? brand.brassSoft : `${brand.slateLight}30`,
                          color: brand.ink950,
                          fontWeight: 700,
                          fontSize: "0.75rem",
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <StatusChip status={item.work_status} />
                    </TableCell>
                    <TableCell sx={{ color: brand.slate, fontSize: "0.85rem", fontFamily: '"IBM Plex Mono", monospace' }}>
                      {item.appointment_date || "—"}
                    </TableCell>
                    <TableCell sx={{ color: brand.slate, fontSize: "0.85rem", fontWeight: 600 }}>
                      {item.appointment_time || "—"}
                    </TableCell>
                    <TableCell sx={{ color: brand.slate, fontSize: "0.82rem" }}>
                      {item.location || "—"}
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" gap={0.8} justifyContent="flex-end">
                        <MuiTooltip title="View Receipt Ticket">
                          <IconButton
                            size="small"
                            onClick={() => openViewDialog(item)}
                            sx={{ bgcolor: `${brand.slateLight}25`, color: brand.ink950 }}
                          >
                            <MdVisibility size={16} />
                          </IconButton>
                        </MuiTooltip>
                        <MuiTooltip title="Edit Appointment">
                          <IconButton
                            size="small"
                            onClick={() => openEdit(item)}
                            sx={{ bgcolor: `${brand.slateLight}25`, color: brand.ink950 }}
                          >
                            <MdEdit size={16} />
                          </IconButton>
                        </MuiTooltip>
                        <MuiTooltip title="Assign Field Staff / Employee">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenAssign(item)}
                            sx={{ bgcolor: `${brand.brassSoft}`, color: brand.ink950 }}
                          >
                            <MdAssignmentInd size={16} />
                          </IconButton>
                        </MuiTooltip>
                        <MuiTooltip title="Delete Appointment">
                          <IconButton
                            size="small"
                            disabled={deletingId === item.id}
                            onClick={() => handleDelete(item.id)}
                            sx={{ bgcolor: `${brand.danger}15`, color: brand.danger }}
                          >
                            {deletingId === item.id ? (
                              <CircularProgress size={16} />
                            ) : (
                              <MdDelete size={16} />
                            )}
                          </IconButton>
                        </MuiTooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                    <MdEventNote size={40} color={brand.slateLight} />
                    <Typography
                      sx={{
                        fontFamily: '"Fraunces", serif',
                        fontWeight: 700,
                        fontSize: "1.1rem",
                        color: brand.ink950,
                        mt: 1,
                      }}
                    >
                      No appointment bookings found
                    </Typography>
                    <Typography sx={{ color: brand.slate, fontSize: "0.85rem", mt: 0.5 }}>
                      {search || filterDate
                        ? "Try clearing your date or search filters."
                        : "Click 'Add Appointment' to create a booking."}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={filteredAppointments.length}
            page={page}
            onPageChange={(e, n) => setPage(n)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 20]}
          />
        </TableContainer>
      ) : (
        /* Grid View */
        <Grid container spacing={3} mb={3}>
          {paginatedAppointments.map((item) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={item.id}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: "22px",
                  bgcolor: brand.paper,
                  border: `1px solid ${brand.slateLight}40`,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  transition: "transform 200ms ease, box-shadow 200ms ease",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: "0 18px 40px -15px rgba(11,10,61,0.12)",
                    borderColor: `${brand.brass}66`,
                  },
                }}
              >
                <Box>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                    <Avatar
                      sx={{
                        width: 44,
                        height: 44,
                        fontSize: "0.9rem",
                        fontWeight: 700,
                        bgcolor: brand.ink950,
                        color: brand.parchment,
                      }}
                    >
                      {initials(item.customer_name) || <MdPerson size={22} />}
                    </Avatar>
                    <Chip
                      label={item.assigned_person || "Unassigned"}
                      size="small"
                      sx={{
                        bgcolor: item.assigned_person ? brand.brassSoft : `${brand.slateLight}30`,
                        color: brand.ink950,
                        fontWeight: 700,
                        fontSize: "0.72rem",
                      }}
                    />
                  </Box>

                  <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", color: brand.ink950 }} noWrap>
                    {item.customer_name}
                  </Typography>

                  <Box mt={1}>
                    <StatusChip status={item.work_status} />
                  </Box>

                  <Box display="flex" alignItems="center" gap={1} mt={1} color={brand.slate}>
                    <MdPhone size={15} />
                    <Typography sx={{ fontSize: "0.82rem", fontFamily: '"IBM Plex Mono", monospace' }}>
                      {item.phone || "—"}
                    </Typography>
                  </Box>

                  <Box display="flex" alignItems="center" gap={1} mt={0.6} color={brand.slate}>
                    <MdAccessTime size={15} />
                    <Typography sx={{ fontSize: "0.82rem", fontFamily: '"IBM Plex Mono", monospace' }}>
                      {item.appointment_date} • {item.appointment_time}
                    </Typography>
                  </Box>

                  {item.location && (
                    <Box display="flex" alignItems="center" gap={1} mt={0.6} color={brand.slate}>
                      <MdLocationOn size={15} />
                      <Typography sx={{ fontSize: "0.82rem" }} noWrap>
                        {item.location}
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Box display="flex" gap={1} justifyContent="flex-end" mt={2.5} pt={2} borderTop={`1px solid ${brand.slateLight}30`}>
                  <Button size="small" variant="outlined" onClick={() => openViewDialog(item)} startIcon={<MdVisibility />}>
                    Ticket
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => openEdit(item)} startIcon={<MdEdit />}>
                    Edit
                  </Button>
                  <Button size="small" variant="contained" color="secondary" onClick={() => handleOpenAssign(item)} startIcon={<MdAssignmentInd />}>
                    Assign
                  </Button>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {/* ── Dialog: Appointment Digital Receipt ─────────────────────────── */}
      <Dialog
        open={openView}
        onClose={() => setOpenView(false)}
        PaperProps={{ sx: { borderRadius: "24px", p: 1, width: "100%", maxWidth: 500 } }}
      >
        <DialogTitle sx={{ fontFamily: '"Fraunces", serif', fontWeight: 700, fontSize: "1.4rem" }}>
          Digital Appointment Ticket
        </DialogTitle>
        <DialogContent dividers>
          {viewData && (
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: "18px",
                bgcolor: brand.paper,
                border: `1.5px dashed ${brand.brass}`,
                boxShadow: "0 10px 30px -10px rgba(11,10,61,0.08)",
              }}
            >
              <Box textAlign="center" pb={2} mb={2} borderBottom={`1px solid ${brand.slateLight}40`}>
                <Typography sx={{ fontFamily: '"Fraunces", serif', fontWeight: 700, fontSize: "1.3rem", color: brand.ink950 }}>
                  OurGenie Appointment Pass
                </Typography>
                <Typography sx={{ fontSize: "0.78rem", color: brand.slate, fontFamily: '"IBM Plex Mono", monospace', mt: 0.5 }}>
                  {viewData.appointment_date} • {viewData.appointment_time}
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography sx={{ fontSize: "0.75rem", color: brand.slate, fontWeight: 700 }}>Customer Name</Typography>
                  <Typography sx={{ fontWeight: 700, color: brand.ink950, fontSize: "0.92rem" }}>
                    {viewData.customer_name}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography sx={{ fontSize: "0.75rem", color: brand.slate, fontWeight: 700 }}>Mobile Contact</Typography>
                  <Typography sx={{ fontWeight: 600, color: brand.ink950, fontSize: "0.88rem", fontFamily: '"IBM Plex Mono", monospace' }}>
                    {viewData.phone}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography sx={{ fontSize: "0.75rem", color: brand.slate, fontWeight: 700 }}>Assigned Staff</Typography>
                  <Typography sx={{ fontWeight: 600, color: brand.ink950, fontSize: "0.88rem" }}>
                    {viewData.assigned_person || "Unassigned"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography sx={{ fontSize: "0.75rem", color: brand.slate, fontWeight: 700, mb: 0.5 }}>Status</Typography>
                  <StatusChip status={viewData.work_status} />
                </Grid>
                <Grid item xs={6}>
                  <Typography sx={{ fontSize: "0.75rem", color: brand.slate, fontWeight: 700 }}>Location</Typography>
                  <Typography sx={{ fontWeight: 600, color: brand.ink950, fontSize: "0.88rem" }}>
                    {viewData.location || "N/A"}
                  </Typography>
                </Grid>

                {viewData.map_url && (
                  <Grid item xs={12}>
                    <Typography sx={{ fontSize: "0.75rem", color: brand.slate, fontWeight: 700 }}>Google Maps Link</Typography>
                    <a href={viewData.map_url} target="_blank" rel="noopener noreferrer" style={{ color: "#3B82F6", fontSize: "0.85rem", textDecoration: "underline" }}>
                      Open Location in Google Maps
                    </a>
                  </Grid>
                )}

                {viewData.comment && (
                  <Grid item xs={12}>
                    <Typography sx={{ fontSize: "0.75rem", color: brand.slate, fontWeight: 700 }}>Staff Notes / Comment</Typography>
                    <Box bgcolor={`${brand.slateLight}20`} p={1.5} borderRadius="10px" mt={0.5}>
                      <Typography sx={{ fontSize: "0.82rem", color: brand.ink950 }}>{viewData.comment}</Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>

              {/* Field Staff Work Tracking & Proof (from the staff app's camera flow) */}
              {(viewData.start_image || viewData.end_image || viewData.start_time || viewData.end_time) && (
                <Box mt={3} bgcolor="#F8FAFC" border={`1px solid ${brand.slateLight}40`} p={2} borderRadius="16px">
                  <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                    <MdAssignmentInd size={18} color={brand.brass} />
                    <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", color: brand.ink950 }}>
                      Field Staff Work Tracking & Proof
                    </Typography>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Box bgcolor="#FFFFFF" p={1.5} borderRadius="12px" border="1px solid #E2E8F0">
                        <Chip label="1. Start Work Proof" size="small" sx={{ bgcolor: "#E0F2FE", color: "#0369A1", fontWeight: 700, fontSize: "0.7rem", mb: 1 }} />
                        {viewData.start_time && (
                          <Typography sx={{ fontSize: "0.72rem", color: brand.slate, mb: 1 }}>
                            Started: {new Date(viewData.start_time).toLocaleString()}
                          </Typography>
                        )}
                        {viewData.start_image ? (
                          <Box
                            component="img"
                            src={getImageUrl(viewData.start_image)}
                            alt="Start Work Photo"
                            sx={{ width: "100%", height: 120, objectFit: "cover", borderRadius: "8px", border: "1px solid #CBD5E1" }}
                          />
                        ) : (
                          <Box height={100} display="flex" alignItems="center" justifyContent="center" bgcolor="#F1F5F9" borderRadius="8px">
                            <Typography sx={{ fontSize: "0.75rem", color: "#94A3B8" }}>No Start Photo</Typography>
                          </Box>
                        )}
                        {(viewData.start_latitude && viewData.start_longitude) ? (
                          <Button
                            size="small"
                            component="a"
                            href={`https://maps.google.com/?q=${viewData.start_latitude},${viewData.start_longitude}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            endIcon={<MdLaunch size={12} />}
                            sx={{ p: 0, mt: 1, fontSize: "0.7rem", textTransform: "none", fontWeight: 700 }}
                          >
                            Open in Google Maps
                          </Button>
                        ) : (
                          <Typography sx={{ fontSize: "0.72rem", color: "#94A3B8", mt: 1 }}>GPS pending</Typography>
                        )}
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box bgcolor="#FFFFFF" p={1.5} borderRadius="12px" border="1px solid #E2E8F0">
                        <Chip label="2. Complete Work Proof" size="small" sx={{ bgcolor: "#DCFCE7", color: "#15803D", fontWeight: 700, fontSize: "0.7rem", mb: 1 }} />
                        {viewData.end_time && (
                          <Typography sx={{ fontSize: "0.72rem", color: brand.slate, mb: 1 }}>
                            Completed: {new Date(viewData.end_time).toLocaleString()}
                          </Typography>
                        )}
                        {viewData.end_image ? (
                          <Box
                            component="img"
                            src={getImageUrl(viewData.end_image)}
                            alt="Complete Work Photo"
                            sx={{ width: "100%", height: 120, objectFit: "cover", borderRadius: "8px", border: "1px solid #CBD5E1" }}
                          />
                        ) : (
                          <Box height={100} display="flex" alignItems="center" justifyContent="center" bgcolor="#F1F5F9" borderRadius="8px">
                            <Typography sx={{ fontSize: "0.75rem", color: "#94A3B8" }}>No Complete Photo</Typography>
                          </Box>
                        )}
                        {(viewData.end_latitude && viewData.end_longitude) ? (
                          <Button
                            size="small"
                            component="a"
                            href={`https://maps.google.com/?q=${viewData.end_latitude},${viewData.end_longitude}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            endIcon={<MdLaunch size={12} />}
                            sx={{ p: 0, mt: 1, fontSize: "0.7rem", textTransform: "none", fontWeight: 700 }}
                          >
                            Open in Google Maps
                          </Button>
                        ) : (
                          <Typography sx={{ fontSize: "0.72rem", color: "#94A3B8", mt: 1 }}>GPS pending</Typography>
                        )}
                        {viewData.notes && (
                          <Typography sx={{ fontSize: "0.75rem", color: brand.ink950, mt: 1, fontStyle: "italic" }}>
                            "{viewData.notes}"
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              )}

              <Box display="flex" gap={1.5} mt={3}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={() => copyReceipt(viewData)}
                  startIcon={<MdContentCopy />}
                  sx={{ borderRadius: "10px", fontWeight: 700 }}
                >
                  Copy Ticket
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => shareReceipt(viewData)}
                  startIcon={<MdShare />}
                  sx={{ borderRadius: "10px", fontWeight: 700, borderColor: brand.ink950, color: brand.ink950 }}
                >
                  Share
                </Button>
              </Box>
            </Paper>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenView(false)} variant="contained" color="secondary" sx={{ fontWeight: 700, borderRadius: "10px" }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog: Assign Field Staff / Employee ─────────────────────────── */}
      <Dialog
        open={openAssignModal}
        onClose={() => setOpenAssignModal(false)}
        PaperProps={{ sx: { borderRadius: "22px", p: 1, width: "100%", maxWidth: 460 } }}
      >
        <DialogTitle sx={{ fontFamily: '"Fraunces", serif', fontWeight: 700, fontSize: "1.3rem" }}>
          Assign Vendor Partner / Field Staff
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <Typography variant="body2" color="text.secondary">
              Assigning service partner for <strong>{assignAppointment?.customer_name}</strong>'s appointment
            </Typography>

            <FormControl fullWidth>
              <InputLabel>Select Assignee (Vendor or Staff)</InputLabel>
              <Select
                value={selectedStaff}
                label="Select Assignee (Vendor or Staff)"
                onChange={(e) => setSelectedStaff(e.target.value)}
              >
                <MenuItem value=""><em>-- Unassigned --</em></MenuItem>
                {employees.map((emp) => {
                  const isVendorEmp = (emp.role || "").toLowerCase() === "vendor";
                  return (
                    <MenuItem key={emp.id} value={emp.name}>
                      <Box display="flex" alignItems="center" gap={1} width="100%">
                        <span>{emp.name}</span>
                        <Chip
                          label={isVendorEmp ? "Vendor Partner" : (emp.role ? emp.role.toUpperCase() : "Staff")}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: "0.68rem",
                            fontWeight: 700,
                            bgcolor: isVendorEmp ? "#8B5CF622" : brand.brassSoft,
                            color: isVendorEmp ? "#6D28D9" : brand.ink950,
                          }}
                        />
                        {emp.mobile && <span style={{ color: brand.slate, fontSize: "0.75rem", marginLeft: "auto" }}>{emp.mobile}</span>}
                      </Box>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            <TextField
              label="Or Type Custom Staff Name"
              placeholder="e.g. Alex Miller"
              value={selectedStaff}
              onChange={(e) => setSelectedStaff(e.target.value)}
              fullWidth
              helperText="Select an employee from the list or enter custom staff name."
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setOpenAssignModal(false)} sx={{ color: brand.slate, fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveAssign}
            variant="contained"
            color="secondary"
            disabled={assigning}
            sx={{ fontWeight: 700, borderRadius: "10px" }}
          >
            {assigning ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Assign & Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog: Add / Edit Appointment ────────────────────────────── */}
      <Dialog
        open={openForm}
        onClose={() => setOpenForm(false)}
        PaperProps={{ sx: { borderRadius: "22px", p: 1, width: "100%", maxWidth: 520 } }}
      >
        <DialogTitle sx={{ fontFamily: '"Fraunces", serif', fontWeight: 700, fontSize: "1.35rem" }}>
          {editing ? "Edit Appointment Booking" : "Create New Appointment"}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={1.5} mt={1}>
            <TextField
              label="Customer Name"
              name="name"
              value={form.name}
              onChange={handleChange}
              required
              fullWidth
            />
            <TextField
              label="Mobile Phone Number"
              name="phone"
              value={form.phone}
              onChange={handleChange}
              required
              fullWidth
            />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  type="date"
                  label="Appointment Date"
                  name="date"
                  value={form.date}
                  onChange={handleChange}
                  required
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  type="time"
                  label="Time Slot"
                  name="time"
                  value={form.time}
                  onChange={handleChange}
                  required
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
            <TextField
              label="Assigned Field Staff"
              name="assigned_person"
              value={form.assigned_person}
              onChange={handleChange}
              fullWidth
            />
            <TextField
              label="Location Description"
              name="location"
              value={form.location}
              onChange={handleChange}
              fullWidth
            />
            <TextField
              label="Google Maps URL"
              name="map_url"
              placeholder="https://maps.google.com/..."
              value={form.map_url}
              onChange={handleChange}
              fullWidth
            />
            <TextField
              label="Booking Comments / Instructions"
              name="comment"
              value={form.comment}
              onChange={handleChange}
              multiline
              rows={3}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setOpenForm(false)} sx={{ color: brand.slate, fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleSubmit}
            disabled={saving}
            sx={{ fontWeight: 700, px: 3, borderRadius: "10px" }}
          >
            {saving ? <CircularProgress size={20} color="inherit" /> : editing ? "Update Booking" : "Save Appointment"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog: Bulk Excel Upload ───────────────────────────────────── */}
      <Dialog
        open={openBulkDialog}
        onClose={() => {
          if (!savingBulk) {
            setOpenBulkDialog(false);
            setBulkPreview([]);
          }
        }}
        PaperProps={{ sx: { borderRadius: "22px", p: 1, width: "100%", maxWidth: 840 } }}
      >
        <DialogTitle sx={{ fontFamily: '"Fraunces", serif', fontWeight: 700, fontSize: "1.4rem" }}>
          Bulk Excel Appointment Upload
        </DialogTitle>

        <DialogContent>
          <Typography sx={{ color: brand.slate, fontSize: "0.85rem", mb: 2 }}>
            Upload multiple appointments at once using an Excel (.xlsx, .xls) or CSV spreadsheet file.
          </Typography>

          <Grid container spacing={2} mb={3}>
            {/* Step 1: Download Template */}
            <Grid item xs={12} sm={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: "16px",
                  bgcolor: brand.paper,
                  border: `1px solid ${brand.slateLight}40`,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <Box mb={2}>
                  <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <Chip label="Step 1" size="small" sx={{ bgcolor: brand.brassSoft, fontWeight: 700 }} />
                    <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: brand.ink950 }}>
                      Download Template
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: "0.8rem", color: brand.slate }}>
                    Get a pre-formatted Excel template with correct columns (Name, Phone, Date, Time, Location, Staff).
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  onClick={handleDownloadTemplate}
                  startIcon={<MdFileDownload size={20} />}
                  sx={{
                    borderRadius: "12px",
                    fontWeight: 700,
                    borderColor: brand.ink950,
                    color: brand.ink950,
                    textTransform: "none",
                  }}
                >
                  Download Sample Excel
                </Button>
              </Paper>
            </Grid>

            {/* Step 2: Upload Excel File */}
            <Grid item xs={12} sm={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: "16px",
                  bgcolor: `${brand.slateLight}10`,
                  border: `2px dashed ${brand.brass}`,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  cursor: "pointer",
                  position: "relative",
                  "&:hover": { bgcolor: brand.brassSoft },
                }}
                component="label"
              >
                <input
                  type="file"
                  accept=".xlsx, .xls, .csv"
                  onChange={handleExcelFileSelect}
                  style={{ display: "none" }}
                  disabled={uploadingExcel}
                />
                {uploadingExcel ? (
                  <Box display="flex" flexDirection="column" alignItems="center">
                    <CircularProgress size={32} sx={{ color: brand.brass, mb: 1 }} />
                    <Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>Parsing Excel File...</Typography>
                  </Box>
                ) : (
                  <>
                    <MdCloudUpload size={36} color={brand.ink950} />
                    <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: brand.ink950, mt: 1 }}>
                      Click or Drag Excel File Here
                    </Typography>
                    <Typography sx={{ fontSize: "0.75rem", color: brand.slate, mt: 0.5 }}>
                      Supports .xlsx, .xls, .csv files
                    </Typography>
                  </>
                )}
              </Paper>
            </Grid>
          </Grid>

          {/* Parsed Preview Section */}
          {bulkPreview.length > 0 && (
            <Box>
              <Divider sx={{ my: 2 }} />

              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5} flexWrap="wrap" gap={1}>
                <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: brand.ink950 }}>
                  Excel Data Preview ({bulkPreview.length} Rows Parsed)
                </Typography>
                <Box display="flex" gap={1}>
                  <Chip
                    icon={<MdCheckCircle size={16} />}
                    label={`${bulkPreview.filter((r) => r.isValid).length} Valid`}
                    size="small"
                    color="success"
                    sx={{ fontWeight: 700 }}
                  />
                  {bulkPreview.filter((r) => !r.isValid).length > 0 && (
                    <Chip
                      icon={<MdWarning size={16} />}
                      label={`${bulkPreview.filter((r) => !r.isValid).length} Invalid (Skipped)`}
                      size="small"
                      color="error"
                      sx={{ fontWeight: 700 }}
                    />
                  )}
                </Box>
              </Box>

              <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                  maxHeight: 280,
                  borderRadius: "14px",
                  border: `1px solid ${brand.slateLight}40`,
                  bgcolor: brand.paper,
                }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>#</TableCell>
                      <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>Status</TableCell>
                      <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>Customer</TableCell>
                      <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>Phone</TableCell>
                      <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>Date</TableCell>
                      <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>Time</TableCell>
                      <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>Staff</TableCell>
                      <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>Location</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bulkPreview.map((row, idx) => (
                      <TableRow key={idx} sx={{ bgcolor: row.isValid ? "inherit" : "#FEF2F2" }}>
                        <TableCell sx={{ fontWeight: 600, fontSize: "0.78rem" }}>{idx + 1}</TableCell>
                        <TableCell>
                          {row.isValid ? (
                            <Chip label="Ready" size="small" color="success" variant="outlined" sx={{ height: 22, fontSize: "0.7rem", fontWeight: 700 }} />
                          ) : (
                            <Chip label="Invalid" size="small" color="error" sx={{ height: 22, fontSize: "0.7rem", fontWeight: 700 }} />
                          )}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: "0.82rem" }}>{row.customer_name || "—"}</TableCell>
                        <TableCell sx={{ fontSize: "0.82rem" }}>{row.phone || "—"}</TableCell>
                        <TableCell sx={{ fontSize: "0.82rem", fontWeight: 600 }}>{row.appointment_date || "—"}</TableCell>
                        <TableCell sx={{ fontSize: "0.82rem" }}>{row.appointment_time || "—"}</TableCell>
                        <TableCell sx={{ fontSize: "0.82rem" }}>{row.assigned_person || "—"}</TableCell>
                        <TableCell sx={{ fontSize: "0.82rem" }}>{row.location || "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => {
              setOpenBulkDialog(false);
              setBulkPreview([]);
            }}
            disabled={savingBulk}
            sx={{ color: brand.slate, fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleBulkSubmit}
            disabled={savingBulk || bulkPreview.filter((r) => r.isValid).length === 0}
            startIcon={savingBulk ? <CircularProgress size={18} color="inherit" /> : <MdFileUpload size={20} />}
            sx={{ fontWeight: 700, px: 3, borderRadius: "10px" }}
          >
            {savingBulk
              ? "Importing..."
              : `Import ${bulkPreview.filter((r) => r.isValid).length} Appointments`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Appointment;
